import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { JarvisStatusProvider } from "@/pages/doserne/jarvisStatus";
import DoserneJarvisMount from "@/pages/doserne/DoserneJarvisMount";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import WorkerRegistrationWizard from "@/pages/WorkerRegistrationWizard";
import AuthCallback from "@/pages/AuthCallback";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Support from "@/pages/Support";
import PublicProjects from "@/pages/PublicProjects";
import PublicProjectDetail from "@/pages/PublicProjectDetail";
import PublicWebshop from "@/pages/public/PublicWebshop";
import PublicRentals from "@/pages/public/PublicRentals";
import CartPage from "@/pages/public/CartPage";
import CheckoutSuccess from "@/pages/public/CheckoutSuccess";
import ProductDetail from "@/pages/public/ProductDetail";
import StillasjobbOslo from "@/pages/public/StillasjobbOslo";
import StillasfirmaOslo from "@/pages/public/StillasfirmaOslo";
import StillasfirmaOsloNew from "@/pages/public/StillasfirmaOsloNew";
import StillasPrivatOslo from "@/pages/public/StillasPrivatOsloNew";
import StillasPrivatOsloClassic from "@/pages/public/StillasPrivatOsloClassic";
import StillasRaskOslo from "@/pages/public/StillasRaskOslo";
import DiscoverPage from "@/pages/public/DiscoverPage";
import PlatformGuide from "@/pages/public/PlatformGuide";
import ScaffolderQuiz from "@/pages/public/ScaffolderQuiz";

// Client Pages (V3 Only)
import ClientRequestProject from "@/pages/client/ClientRequestProject";
import ClientProjectsV3 from "@/pages/client/ClientProjectsV3";
import ClientProjectDetailV3 from "@/pages/client/ClientProjectDetailV3";
import ClientContractSignV3 from "@/pages/client/ClientContractSignV3";
import ClientContracts from "@/pages/client/ClientContracts";
import ClientDashboard from "@/pages/client/ClientDashboard";
import ClientStillastjenester from "@/pages/client/ClientStillastjenester";
import CompanyRegistration from "@/pages/client/CompanyRegistration";
import ContractSign from "@/pages/client/ContractSign";
import ClientProfile from "@/pages/client/ClientProfile";

// Worker Pages
import WorkerDashboard from "@/pages/worker/WorkerDashboard";
import WorkerAssignments from "@/pages/worker/WorkerAssignments";
import WorkerTimesheets from "@/pages/worker/WorkerTimesheets";
import WorkerProfile from "@/pages/worker/WorkerProfile";
import WorkerAgreements from "@/pages/worker/WorkerAgreements";
import WorkerEarnings from "@/pages/worker/WorkerEarnings";
import WorkerApplications from "@/pages/worker/WorkerApplications";
import WorkerMyProjects from "@/pages/worker/WorkerMyProjects";
import WorkerSignContract from "@/pages/worker/WorkerSignContract";
import ProjectContractSign from "@/pages/worker/ProjectContractSign";
import EmploymentContract from "@/pages/worker/EmploymentContract";
import AgreementDetail from "@/pages/AgreementDetail";
import ViewSignedContract from "@/pages/shared/ViewSignedContract";

// Team Leader Pages
import TeamLeaderDashboard from "@/pages/teamleader/TeamLeaderDashboard";
import TeamLeaderProjectDetail from "@/pages/teamleader/TeamLeaderProjectDetail";
import TeamLeaderTeams from "@/pages/teamleader/TeamLeaderTeams";
import TeamLeaderAgreements from "@/pages/teamleader/TeamLeaderAgreements";
import FindWorkers from "@/pages/teamleader/FindWorkers";
import BrowseLiveProjects from "@/pages/teamleader/BrowseLiveProjects";
import BrowseProjectDetail from "@/pages/teamleader/BrowseProjectDetail";
import TeamMyProjects from "@/pages/teamleader/TeamMyProjects";
import TeamProjectDetail from "@/pages/teamleader/TeamProjectDetail";
import TeamSignContract from "@/pages/teamleader/TeamSignContract";

// Admin Pages
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminProjectDetailV2 from "@/pages/admin/AdminProjectDetailV2";
import AdminWorkers from "@/pages/admin/AdminWorkers";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminPromotions from "@/pages/admin/AdminPromotions";
import AdminEarnings from "@/pages/admin/AdminEarnings";
import AdminProjectApproval from "@/pages/admin/AdminProjectApprovalListV2";
import AdminProjectApprovalDetail from "@/pages/admin/AdminProjectApprovalV2";
import AdminProjectApplications from "@/pages/admin/AdminProjectApplications";
import AdminWebshop from "@/pages/admin/AdminWebshop";
import AdminRentals from "@/pages/admin/AdminRentals";
import AdminRoles from "@/pages/admin/AdminRoles";
import AdminContracts from "@/pages/admin/AdminContracts";
import AdminEmploymentContracts from "@/pages/admin/AdminEmploymentContracts";
import LocalProjects from "@/pages/admin/LocalProjects";
import LocalProjectDetail from "@/pages/admin/LocalProjectDetail";
import LocalControlReport from "@/pages/admin/LocalControlReport";
import LocalProjectSJA from "@/pages/admin/LocalProjectSJA";
import AdminWebshopAppearance from "@/pages/admin/AdminWebshopAppearance";
import AdminMarketplace from "@/pages/admin/AdminMarketplace";
import AdminPayouts from "@/pages/admin/AdminPayouts";
import AdminRoadmap from "@/pages/admin/AdminRoadmap";
import AdminHolding from "@/pages/admin/AdminHolding";
import AdminLandingPage from "@/pages/admin/AdminLandingPage";
import AdminBudgetPlanning from "@/pages/admin/AdminBudgetPlanning";
import DatabaseViewer from "@/pages/admin/DatabaseViewer";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminBanner from "@/pages/admin/AdminBanner";
import AdminScrollAnalytics from "@/pages/admin/AdminScrollAnalytics";

// Seller Pages (Marketplace)
import SellerRegister from "@/pages/seller/SellerRegister";
import SellerDashboard from "@/pages/seller/SellerDashboard";
import SellerProducts from "@/pages/seller/SellerProducts";
import SellerSettings from "@/pages/seller/SellerSettings";
import SellerPublicPage from "@/pages/public/SellerPublicPage";

// Testimonials
import TestimonialSubmit from "@/pages/public/TestimonialSubmit";
import PublicTestimonials from "@/pages/public/PublicTestimonials";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminContractSettings from "@/pages/admin/AdminContractSettings";

// Underlag (Doserne Stillas timesheet)
import UnderlagForm from "@/pages/underlag/UnderlagForm";
import AppsHub from "@/pages/apps/AppsHub";
import KontrollForm from "@/pages/apps/kontroll/KontrollForm";
import KontrollSign from "@/pages/apps/kontroll/KontrollSign";
import SJAForm from "@/pages/apps/sja/SJAForm";
import SJASign from "@/pages/apps/sja/SJASign";
import DoserneHome from "@/pages/doserne/DoserneHome";
import DoserneAccessGuard from "@/pages/doserne/DoserneAccessGuard";
import DoserneDailyPlan from "@/pages/doserne/DoserneDailyPlan";
import DoserneLogin from "@/pages/doserne/DoserneLogin";
import DoserneOpenInAppBanner from "@/pages/doserne/OpenInAppBanner";
import DoserneResetPassword from "@/pages/doserne/DoserneResetPassword";
import DoserneVerifyLink from "@/pages/doserne/DoserneVerifyLink";
import DoserneProjects from "@/pages/doserne/DoserneProjects";
import DoserneKunder from "@/pages/doserne/DoserneKunder";
import DoserneColleagues from "@/pages/doserne/DoserneColleagues";
import DoserneTimesheetAdmin from "@/pages/doserne/DoserneTimesheetAdmin";
import DoserneTimesheet from "@/pages/doserne/DoserneTimesheet";
import DoserneContractSignPublic from "@/pages/doserne/DoserneContractSignPublic";
import DoserneProjectDetail from "@/pages/doserne/DoserneProjectDetail";
import DoserneProgress from "@/pages/doserne/DoserneProgress";
import DoserneProfile from "@/pages/doserne/DoserneProfile";
import DoserneComingSoon from "@/pages/doserne/ComingSoon";
import DoserneKurs from "@/pages/doserne/DoserneKurs";
import DoserneCourseView from "@/pages/doserne/DoserneCourseView";
import DoserneCourseAck from "@/pages/doserne/DoserneCourseAck";
import DoserneAgreementSign from "@/pages/doserne/DoserneAgreementSign";
import DoserneMyCertificates from "@/pages/doserne/DoserneMyCertificates";
import DoserneCertificateVerify from "@/pages/doserne/DoserneCertificateVerify";
import DoserneAdminKursSettings from "@/pages/doserne/DoserneAdminKursSettings";
import DoserneTripletexConfig from "@/pages/doserne/DoserneTripletexConfig";
import DoserneKlaer from "@/pages/doserne/DoserneKlaer";
import DoserneLager from "@/pages/doserne/DoserneLager";
import DoserneSJA from "@/pages/doserne/DoserneSJA";
import LagerRequestPublic from "@/pages/doserne/LagerRequestPublic";
import DoserneChat from "@/pages/doserne/DoserneChat";
import DoserneReports from "@/pages/doserne/DoserneReports";
import DoserneImportant from "@/pages/doserne/DoserneImportant";
import DoserneContracts from "@/pages/doserne/DoserneContracts";
import DoserneEmail from "@/pages/doserne/DoserneEmail";
import PublicDailyPlan from "@/pages/public/PublicDailyPlan";
import DoserneOnboarding from "@/pages/doserne/DoserneOnboarding";
import DoserneJarvisCosts from "@/pages/doserne/DoserneJarvisCosts";
import CapacityPlanner from "@/pages/admin/CapacityPlanner";
import ProjectOverview from "@/pages/admin/ProjectOverview2";
import AdminPermissions from "@/pages/admin/AdminPermissions";
import { DoserneAuthProvider } from "@/pages/doserne/doserneAuth";
import { LanguageProvider } from "@/pages/doserne/i18n";
import UnderlagSign from "@/pages/underlag/UnderlagSign";
import ClientPortalProject from "@/pages/client-portal/ClientPortalProject";
import ClientPortalCustomer from "@/pages/client-portal/ClientPortalCustomer";
import AdminUnderlag from "@/pages/admin/AdminUnderlag";

// Holding Admin Pages
import HoldingDashboard from "@/pages/holding/HoldingDashboard";
import HoldingProjects from "@/pages/holding/HoldingProjects";
import HoldingProjectDetail from "@/pages/holding/HoldingProjectDetail";
import HoldingStillastjenester from "@/pages/holding/HoldingStillastjenester";
import HoldingPublicQuotes from "@/pages/holding/HoldingPublicQuotes";
import HoldingInternalEmployees from "@/pages/holding/HoldingInternalEmployees";
import HoldingContracts from "@/pages/holding/HoldingContracts";
import HoldingContractGenerator from "@/pages/holding/HoldingContractGenerator";
import HoldingQuizResults from "@/pages/holding/HoldingQuizResults";
import HoldingProfile from "@/pages/holding/HoldingProfile";
import HoldingFinances from "@/pages/holding/HoldingFinances";
import HoldingWorkers from "@/pages/holding/HoldingWorkers";

// Shared Pages
import ScaffoldControlReport from "@/pages/ScaffoldControlReport";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { PermissionProvider } from "@/context/PermissionContext";

// Components
import ChatWidget from "@/components/ChatWidget";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import ImpersonationBanner from "@/components/ImpersonationBanner";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    const dashboardRoutes = {
      platform_admin: "/admin",
      operations_manager: "/admin",
      holding_admin: "/holding",
      team_leader: "/team-leader",
      worker: "/worker",
      client_admin: "/client",
      client_viewer: "/client",
    };
    return <Navigate to={dashboardRoutes[user.role] || "/"} replace />;
  }

  return children;
};

// Analytics Tracker Component
const AnalyticsTracker = () => {
  const location = useLocation();
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Track page enter time
    const pageEnterTime = Date.now();
    let timeAlreadySent = false;
    
    // Track page view
    const trackPageView = async () => {
      try {
        await fetch(`${API}/api/analytics/track`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            page: location.pathname,
            referrer: document.referrer || null,
            user_agent: navigator.userAgent
          })
        });
      } catch (error) {
        // Silently fail - don't disrupt user experience
        console.debug("Analytics tracking failed:", error);
      }
    };

    trackPageView();
    
    // Track time spent when leaving page
    const trackTimeSpent = async (useBeacon = true) => {
      if (timeAlreadySent) return;
      
      const timeSpent = Math.floor((Date.now() - pageEnterTime) / 1000);
      if (timeSpent <= 0 || timeSpent >= 1800) return;
      
      const data = JSON.stringify({ time_spent: timeSpent });
      
      // Try sendBeacon first (works on page close)
      if (useBeacon && navigator.sendBeacon) {
        const blob = new Blob([data], { type: 'application/json' });
        const sent = navigator.sendBeacon(`${API}/api/analytics/time-spent`, blob);
        if (sent) {
          timeAlreadySent = true;
          return;
        }
      }
      
      // Fallback to fetch
      try {
        await fetch(`${API}/api/analytics/time-spent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: data,
          keepalive: true // Important for page unload
        });
        timeAlreadySent = true;
      } catch (e) {
        console.debug("Time tracking failed:", e);
      }
    };
    
    // Track on page visibility change (tab switch, minimize)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackTimeSpent(true);
      }
    };
    
    // Track before page unload
    const handleBeforeUnload = () => {
      trackTimeSpent(true);
    };
    
    // Track on page hide (better support for mobile Safari)
    const handlePageHide = () => {
      trackTimeSpent(true);
    };
    
    // Periodic heartbeat every 30 seconds (catches users who stay long)
    const heartbeatInterval = setInterval(() => {
      const timeSpent = Math.floor((Date.now() - pageEnterTime) / 1000);
      if (timeSpent > 0 && timeSpent < 1800) {
        fetch(`${API}/api/analytics/time-spent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ time_spent: timeSpent })
        }).catch(() => {});
      }
    }, 30000); // Every 30 seconds
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide); // Better for Safari
    
    return () => {
      clearInterval(heartbeatInterval);
      trackTimeSpent(false); // Use fetch on cleanup (route change)
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [location.pathname, API]);

  return null;
};

// App Router Component
function AppRouter() {
  const location = useLocation();

  // Check for OAuth session_id in URL fragment
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <JarvisStatusProvider>
      <AnalyticsTracker />
      <ImpersonationBanner />
      {location.pathname.startsWith("/doserne") && <DoserneOpenInAppBanner />}
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/projects" element={<PublicProjects />} />
      <Route path="/lager/r/:code" element={<LagerRequestPublic />} />
      <Route path="/projects/:projectId" element={<PublicProjectDetail />} />
      <Route path="/webshop" element={<PublicWebshop />} />
      <Route path="/nettbutikk" element={<PublicWebshop />} />
      <Route path="/webshop/product/:productId" element={<ProductDetail />} />
      <Route path="/nettbutikk/product/:productId" element={<ProductDetail />} />
      <Route path="/nettbutikk/seller/:sellerCode" element={<SellerPublicPage />} />
      <Route path="/webshop/seller/:sellerCode" element={<SellerPublicPage />} />
      <Route path="/marketplace/:sellerCode" element={<SellerPublicPage />} />

      {/* Testimonials (public) */}
      <Route path="/anmeldelse/:token" element={<TestimonialSubmit />} />
      <Route path="/anmeldelser" element={<PublicTestimonials />} />
      
      {/* Seller Routes (Marketplace) */}
      <Route path="/seller/register" element={<SellerRegister />} />
      <Route path="/bli-selger" element={<SellerRegister />} />
      <Route path="/seller/dashboard" element={<SellerDashboard />} />
      <Route path="/seller/products" element={<SellerProducts />} />
      <Route path="/seller/orders" element={<SellerDashboard />} />
      <Route path="/seller/analytics" element={<SellerDashboard />} />
      <Route path="/seller/payouts" element={<SellerDashboard />} />
      <Route path="/seller/settings" element={<SellerSettings />} />
      
      <Route path="/rentals" element={<PublicRentals />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/register/worker" element={<WorkerRegistrationWizard />} />
      <Route path="/bli-stillasmontor" element={<WorkerRegistrationWizard />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/personvern" element={<PrivacyPolicy />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/support" element={<Support />} />
      <Route path="/kundeservice" element={<Support />} />
      <Route path="/hjelp" element={<Support />} />
      <Route path="/forgot-password" element={<ResetPassword />} />
      
      {/* SEO Landing Pages */}
      <Route path="/stillasjobb-oslo" element={<StillasjobbOslo />} />
      <Route path="/stillasfirma-oslo" element={<StillasfirmaOsloNew />} />
      <Route path="/stillasfirma-oslo-classic" element={<StillasfirmaOslo />} />
      <Route path="/stillas-privat-oslo" element={<StillasPrivatOslo />} />
      <Route path="/stillas-privat-oslo-classic" element={<StillasPrivatOsloClassic />} />
      <Route path="/stillas-rask-oslo" element={<StillasRaskOslo />} />
      <Route path="/discover" element={<DiscoverPage />} />
      <Route path="/les-mer" element={<DiscoverPage />} />
      <Route path="/hvordan-det-fungerer" element={<PlatformGuide />} />
      <Route path="/platform-guide" element={<PlatformGuide />} />
      <Route path="/learn-more" element={<DiscoverPage />} />
      <Route path="/quiz" element={<ScaffolderQuiz />} />
      <Route path="/scaffolder-quiz" element={<ScaffolderQuiz />} />

      {/* Client Routes - V3 Only (Holding Flow) */}
      <Route
        path="/client"
        element={
          <ProtectedRoute allowedRoles={["client_admin", "client_viewer"]}>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/projects"
        element={
          <ProtectedRoute allowedRoles={["client_admin", "client_viewer"]}>
            <ClientProjectsV3 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/request-project"
        element={
          <ProtectedRoute allowedRoles={["client_admin"]}>
            <ClientRequestProject />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/projects/:projectId"
        element={
          <ProtectedRoute allowedRoles={["client_admin", "client_viewer"]}>
            <ClientProjectDetailV3 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/projects-v3"
        element={
          <ProtectedRoute allowedRoles={["client_admin", "client_viewer"]}>
            <ClientProjectsV3 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/projects-v3/:projectId"
        element={
          <ProtectedRoute allowedRoles={["client_admin", "client_viewer"]}>
            <ClientProjectDetailV3 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/projects-v3/:projectId/sign"
        element={
          <ProtectedRoute allowedRoles={["client_admin"]}>
            <ClientContractSignV3 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/stillastjenester"
        element={
          <ProtectedRoute allowedRoles={["client_admin", "client_viewer"]}>
            <ClientStillastjenester />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/company-registration"
        element={
          <ProtectedRoute allowedRoles={["client_admin"]}>
            <CompanyRegistration />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/contracts"
        element={
          <ProtectedRoute allowedRoles={["client_admin", "client_viewer"]}>
            <ClientContracts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/contracts/:contractId"
        element={
          <ProtectedRoute allowedRoles={["client_admin", "client_viewer"]}>
            <ContractSign />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/profile"
        element={
          <ProtectedRoute allowedRoles={["client_admin"]}>
            <ClientProfile />
          </ProtectedRoute>
        }
      />

      {/* Worker Routes */}
      <Route
        path="/worker"
        element={
          <ProtectedRoute allowedRoles={["worker"]}>
            <WorkerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worker/assignments"
        element={
          <ProtectedRoute allowedRoles={["worker"]}>
            <WorkerAssignments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worker/timesheets"
        element={
          <ProtectedRoute allowedRoles={["worker"]}>
            <WorkerTimesheets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worker/profile"
        element={
          <ProtectedRoute allowedRoles={["worker"]}>
            <WorkerProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worker/agreements"
        element={
          <ProtectedRoute allowedRoles={["worker"]}>
            <TeamLeaderAgreements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worker/earnings"
        element={
          <ProtectedRoute allowedRoles={["worker"]}>
            <WorkerEarnings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worker/employment-contract"
        element={
          <ProtectedRoute allowedRoles={["worker", "team_leader"]}>
            <EmploymentContract />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worker/browse-projects"
        element={
          <ProtectedRoute allowedRoles={["worker"]}>
            <BrowseLiveProjects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worker/browse-projects/:projectId"
        element={
          <ProtectedRoute allowedRoles={["worker"]}>
            <BrowseProjectDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worker/my-projects"
        element={
          <ProtectedRoute allowedRoles={["worker"]}>
            <TeamMyProjects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worker/project/:projectId"
        element={
          <ProtectedRoute allowedRoles={["worker"]}>
            <TeamProjectDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worker/sign-contract/:projectId"
        element={
          <ProtectedRoute allowedRoles={["worker"]}>
            <WorkerSignContract />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worker/contract/:projectId"
        element={
          <ProtectedRoute allowedRoles={["worker", "team_leader"]}>
            <ViewSignedContract />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-leader/contract/:projectId"
        element={
          <ProtectedRoute allowedRoles={["worker", "team_leader"]}>
            <ViewSignedContract />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worker/applications"
        element={
          <ProtectedRoute allowedRoles={["worker", "team_leader"]}>
            <WorkerApplications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-projects"
        element={
          <ProtectedRoute allowedRoles={["worker", "team_leader"]}>
            <TeamMyProjects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-projects/:projectId"
        element={
          <ProtectedRoute allowedRoles={["worker", "team_leader"]}>
            <TeamProjectDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-projects/:projectId/sign"
        element={
          <ProtectedRoute allowedRoles={["worker", "team_leader"]}>
            <ProjectContractSign />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agreement/:agreementId"
        element={
          <ProtectedRoute allowedRoles={["worker", "team_leader", "platform_admin", "operations_manager"]}>
            <AgreementDetail />
          </ProtectedRoute>
        }
      />

      {/* Team Leader Routes */}
      <Route
        path="/team-leader"
        element={
          <ProtectedRoute allowedRoles={["team_leader"]}>
            <TeamLeaderDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-leader/teams"
        element={
          <ProtectedRoute allowedRoles={["team_leader"]}>
            <TeamLeaderTeams />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-leader/find-workers"
        element={
          <ProtectedRoute allowedRoles={["team_leader"]}>
            <FindWorkers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-leader/agreements"
        element={
          <ProtectedRoute allowedRoles={["team_leader"]}>
            <TeamLeaderAgreements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-leader/projects/:projectId"
        element={
          <ProtectedRoute allowedRoles={["team_leader"]}>
            <TeamLeaderProjectDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-leader/profile"
        element={
          <ProtectedRoute allowedRoles={["team_leader"]}>
            <WorkerProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-leader/earnings"
        element={
          <ProtectedRoute allowedRoles={["team_leader"]}>
            <WorkerEarnings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-leader/browse-projects"
        element={
          <ProtectedRoute allowedRoles={["team_leader", "worker"]}>
            <BrowseLiveProjects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-leader/browse-projects/:projectId"
        element={
          <ProtectedRoute allowedRoles={["team_leader", "worker"]}>
            <BrowseProjectDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-leader/my-projects"
        element={
          <ProtectedRoute allowedRoles={["team_leader", "worker"]}>
            <TeamMyProjects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-leader/my-projects/:projectId"
        element={
          <ProtectedRoute allowedRoles={["team_leader", "worker"]}>
            <TeamProjectDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-leader/sign-contract/:projectId"
        element={
          <ProtectedRoute allowedRoles={["team_leader"]}>
            <TeamSignContract />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <AdminProjects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/:projectId/detail"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <AdminProjectDetailV2 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/:projectId/applications"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <AdminProjectApplications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/workers"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <AdminWorkers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/invoices"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <AdminInvoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/applications"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <AdminApplications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/promotions"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <AdminPromotions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/earnings"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <AdminEarnings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/project-approval"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <AdminProjectApproval />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/project-approval/:projectId"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <AdminProjectApprovalDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/webshop"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "webshop_admin"]}>
            <AdminWebshop />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/webshop/*"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "webshop_admin"]}>
            <AdminWebshop />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/webshop-appearance"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "webshop_admin"]}>
            <AdminWebshopAppearance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/marketplace"
        element={
          <ProtectedRoute allowedRoles={["platform_admin"]}>
            <AdminMarketplace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sellers"
        element={
          <ProtectedRoute allowedRoles={["platform_admin"]}>
            <AdminMarketplace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payouts"
        element={
          <ProtectedRoute allowedRoles={["platform_admin"]}>
            <AdminPayouts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/testimonials"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <AdminTestimonials />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/contract-settings"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "holding_admin", "operations_manager"]}>
            <AdminContractSettings />
          </ProtectedRoute>
        }
      />
      {/* Underlag + apps: private under Doserne login (course-gated). Token-based sign/edit links stay open. */}
      <Route path="/underlag" element={<DoserneAccessGuard><UnderlagForm /></DoserneAccessGuard>} />
      <Route path="/apps" element={<DoserneAccessGuard><AppsHub /></DoserneAccessGuard>} />
      <Route path="/apps/kontroll" element={<DoserneAccessGuard><KontrollForm /></DoserneAccessGuard>} />
      <Route path="/apps/kontroll/edit/:editToken" element={<DoserneAccessGuard><KontrollForm /></DoserneAccessGuard>} />
      <Route path="/apps/kontroll/sign/:signToken" element={<KontrollSign />} />
      <Route path="/apps/sja" element={<DoserneAccessGuard><SJAForm /></DoserneAccessGuard>} />
      <Route path="/apps/sja/edit/:editToken" element={<DoserneAccessGuard><SJAForm /></DoserneAccessGuard>} />
      <Route path="/apps/sja/sign/:signToken" element={<SJASign />} />
      <Route path="/doserne" element={<DoserneAuthProvider><DoserneHome /></DoserneAuthProvider>} />
      <Route path="/doserne/logg-inn" element={<DoserneAuthProvider><DoserneLogin /></DoserneAuthProvider>} />
      <Route path="/doserne/reset-password" element={<DoserneResetPassword />} />
      <Route path="/doserne/kontrakt/:token" element={<DoserneContractSignPublic />} />
      <Route path="/doserne/verifiser" element={<DoserneAuthProvider><DoserneVerifyLink /></DoserneAuthProvider>} />
      <Route path="/doserne/plan" element={<DoserneAuthProvider><DoserneDailyPlan /></DoserneAuthProvider>} />
      <Route path="/doserne/prosjekter" element={<DoserneAuthProvider><DoserneProjects /></DoserneAuthProvider>} />
      <Route path="/doserne/prosjekter/:id" element={<DoserneAuthProvider><DoserneProjectDetail /></DoserneAuthProvider>} />
      <Route path="/doserne/kunder" element={<DoserneAuthProvider><DoserneKunder /></DoserneAuthProvider>} />
      <Route path="/doserne/progresjon" element={<DoserneAuthProvider><DoserneProgress /></DoserneAuthProvider>} />
      <Route path="/doserne/profil" element={<DoserneAuthProvider><DoserneProfile /></DoserneAuthProvider>} />
      <Route path="/doserne/kolleger" element={<DoserneAuthProvider><DoserneColleagues /></DoserneAuthProvider>} />
      <Route path="/doserne/pontaj" element={<DoserneAuthProvider><DoserneTimesheetAdmin /></DoserneAuthProvider>} />
      <Route path="/doserne/timeliste" element={<DoserneAuthProvider><DoserneTimesheet /></DoserneAuthProvider>} />
      <Route path="/doserne/tripletex-config" element={<DoserneAuthProvider><DoserneTripletexConfig /></DoserneAuthProvider>} />
      <Route path="/doserne/klaer" element={<DoserneAuthProvider><DoserneKlaer /></DoserneAuthProvider>} />
      <Route path="/doserne/lager" element={<DoserneAuthProvider><DoserneLager /></DoserneAuthProvider>} />
      <Route path="/doserne/sja" element={<DoserneAuthProvider><DoserneSJA /></DoserneAuthProvider>} />
      <Route path="/doserne/chat" element={<DoserneAuthProvider><DoserneChat /></DoserneAuthProvider>} />
      <Route path="/doserne/sesizari" element={<DoserneAuthProvider><DoserneReports /></DoserneAuthProvider>} />
      <Route path="/doserne/admin/important" element={<DoserneAuthProvider><DoserneImportant /></DoserneAuthProvider>} />
      <Route path="/doserne/admin/kontrakter" element={<DoserneAuthProvider><DoserneContracts /></DoserneAuthProvider>} />
      <Route path="/doserne/email" element={<DoserneAuthProvider><DoserneEmail /></DoserneAuthProvider>} />
      <Route path="/doserne/consum-jarvis" element={<DoserneAuthProvider><DoserneJarvisCosts /></DoserneAuthProvider>} />
      <Route path="/doserne/kurs" element={<DoserneAuthProvider><DoserneKurs /></DoserneAuthProvider>} />
      <Route path="/doserne/admin/kurs" element={<DoserneAuthProvider><DoserneAdminKursSettings /></DoserneAuthProvider>} />
      <Route path="/p/plan/:slug" element={<PublicDailyPlan />} />
      <Route path="/doserne/kurs/sertifikater" element={<DoserneAuthProvider><DoserneComingSoon /></DoserneAuthProvider>} />
      <Route path="/doserne/kurs/:code" element={<DoserneAuthProvider><DoserneCourseView /></DoserneAuthProvider>} />
      <Route path="/doserne/kurs/:code/sign" element={<DoserneAuthProvider><DoserneCourseAck /></DoserneAuthProvider>} />
      <Route path="/doserne/avtale/:code" element={<DoserneAuthProvider><DoserneAgreementSign /></DoserneAuthProvider>} />
      <Route path="/doserne/kursbevis" element={<DoserneAuthProvider><DoserneMyCertificates /></DoserneAuthProvider>} />
      <Route path="/doserne/verify/:certNumber" element={<DoserneCertificateVerify />} />
      <Route path="/admin/capacity" element={<DoserneAuthProvider><CapacityPlanner /></DoserneAuthProvider>} />
      <Route path="/admin/project-overview" element={<DoserneAuthProvider><ProjectOverview /></DoserneAuthProvider>} />
      <Route
        path="/admin/permissions"
        element={
          <ProtectedRoute allowedRoles={["platform_admin"]}>
            <AdminPermissions />
          </ProtectedRoute>
        }
      />
      <Route path="/underlag/edit/:editToken" element={<DoserneAccessGuard><UnderlagForm /></DoserneAccessGuard>} />
      <Route path="/underlag/sign/:signToken" element={<UnderlagSign />} />
      <Route path="/u/:slug" element={<UnderlagSign />} />
      <Route path="/klient/p/:token" element={<ClientPortalProject />} />
      <Route path="/klient/c/:customerToken" element={<ClientPortalCustomer />} />
      <Route path="/klient/c/:customerToken/p/:projectId" element={<ClientPortalProject />} />
      <Route
        path="/admin/underlag"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "holding_admin", "operations_manager"]}>
            <AdminUnderlag />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/roles"
        element={
          <ProtectedRoute allowedRoles={["platform_admin"]}>
            <AdminRoles />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/roadmap"
        element={
          <ProtectedRoute allowedRoles={["platform_admin"]}>
            <AdminRoadmap />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/holding"
        element={
          <ProtectedRoute allowedRoles={["platform_admin"]}>
            <AdminHolding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/budget-planning"
        element={
          <ProtectedRoute allowedRoles={["platform_admin"]}>
            <AdminBudgetPlanning />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/database"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <DatabaseViewer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "holding_admin"]}>
            <AdminAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/banner"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "holding_admin"]}>
            <AdminBanner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/scroll-analytics"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "holding_admin"]}>
            <AdminScrollAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/landing-page"
        element={
          <ProtectedRoute allowedRoles={["platform_admin"]}>
            <AdminLandingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/contracts"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager"]}>
            <AdminContracts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/employment-contracts"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "hr_manager"]}>
            <AdminEmploymentContracts />
          </ProtectedRoute>
        }
      />
      
      {/* Local Projects Routes - access controlled internally */}
      <Route
        path="/local-projects"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "team_leader", "scaffolder", "worker", "client"]}>
            <LocalProjects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/local-projects/:projectId"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "team_leader", "scaffolder", "worker", "client"]}>
            <LocalProjectDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/local-projects/:projectId/control-report/new"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "team_leader", "scaffolder", "worker", "client"]}>
            <LocalControlReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/local-projects/:projectId/control-report/:reportId"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "team_leader", "scaffolder", "worker", "client"]}>
            <LocalControlReport />
          </ProtectedRoute>
        }
      />
      
      {/* Local Project SJA Routes */}
      <Route
        path="/local-projects/:projectId/sja/new"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "team_leader", "scaffolder", "worker", "client"]}>
            <LocalProjectSJA />
          </ProtectedRoute>
        }
      />
      <Route
        path="/local-projects/:projectId/sja/:sjaId"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "team_leader", "scaffolder", "worker", "client"]}>
            <LocalProjectSJA />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/rentals"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "rentals_admin"]}>
            <AdminRentals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/rentals/*"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "rentals_admin"]}>
            <AdminRentals />
          </ProtectedRoute>
        }
      />

      {/* Holding Admin Routes */}
      <Route
        path="/holding"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/projects"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingProjects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/projects/:projectId"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingProjectDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/contracts"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingContracts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/finances"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingFinances />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/workers"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingWorkers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/stillastjenester"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingStillastjenester />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/stillastjenester/:requestId"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingStillastjenester />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/public-quotes"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingPublicQuotes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/public-quotes/:requestId"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingPublicQuotes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/internal-employees"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingInternalEmployees />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/contract-generator"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingContractGenerator />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/quiz-results"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingQuizResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holding/profile"
        element={
          <ProtectedRoute allowedRoles={["holding_admin", "platform_admin"]}>
            <HoldingProfile />
          </ProtectedRoute>
        }
      />

      {/* Scaffold Control Report - accessible by multiple roles */}
      <Route
        path="/projects/:projectId/control-reports"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "team_leader", "worker"]}>
            <ScaffoldControlReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/control-reports/new"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "team_leader", "worker"]}>
            <ScaffoldControlReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/control-reports/:reportId"
        element={
          <ProtectedRoute allowedRoles={["platform_admin", "operations_manager", "team_leader", "worker"]}>
            <ScaffoldControlReport />
          </ProtectedRoute>
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
      <DoserneJarvisMount />
    </JarvisStatusProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <PermissionProvider>
              <AppRouter />
              <ChatWidget />
              <PWAInstallPrompt />
              <Toaster position="bottom-center" richColors />
            </PermissionProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
